import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { IonicModule, ModalController, Platform, ToastController } from '@ionic/angular';
import { AuthService, HelperProvider } from 'src/app/providers';
import { environment } from 'src/environments/environment';
import { Validators, FormGroup, FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { EValid, mapTypes } from 'src/app/app.utils';
import { LocaLstorageService } from 'src/app/store/localstorage.service';
import { GroupBindStoreService } from 'src/app/store/group-bind-store.service';
import { CommonModule } from '@angular/common';
import { DmStoreService } from 'src/app/store/dm-store.service';
import { UmStoreService } from 'src/app/store/um-store.service';
import { MapType } from '@capacitor/google-maps';
import { MapsService } from 'src/app/providers/maps/maps.service';

@Component({
  selector: 'app-settings-modal',
  templateUrl: './settings.modal.html',
  styleUrls: ['./settings.modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [IonicModule, CommonModule, ReactiveFormsModule, FormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [HelperProvider, FormControl],
})
export class SettingsModalComponent implements OnInit {
  public appVersion = environment.version;
  public logoSrc = './assets/imgs/app-icon.png';
  public selectedMapType: MapType;
  public selectedAutocalibration: boolean;
  public selectedDarkMode: boolean;
  public selectedCustomId: boolean;
  public selectedLightUpQR: boolean;
  public selectedInterval: boolean;
  public selectedSnapToRoad: boolean;

  public native: boolean;

  public settingsControls = new FormGroup({
    selectedIntervalMControl: new FormControl(this.localStorageService.intervalMeters, Validators.compose([
      Validators.required,
      Validators.min(0.5),
      Validators.max(100),
      Validators.pattern('^[0-9.]+$'),
    ])),
  });

  public mapTypes = mapTypes;

  private positioningSettingsChanged = false;

  constructor(
    private modalController: ModalController,
    private auth: AuthService,
    private maps: MapsService,
    private localStorageService: LocaLstorageService,
    private cb: ChangeDetectorRef,
    private groupBindStore: GroupBindStoreService,
    private toastController: ToastController,
    private helper: HelperProvider,
    private platform: Platform,
    private dmStore: DmStoreService,
    private umStore: UmStoreService,
  ) { }

  ngOnInit() {
    this.selectedMapType = this.localStorageService.mapType;
    this.selectedAutocalibration = this.localStorageService.autoCalibration;
    this.selectedDarkMode = this.localStorageService.darkMode;
    this.selectedCustomId = this.localStorageService.customIdGeneration;
    this.selectedLightUpQR = this.localStorageService.lightUpQR;
    this.selectedInterval = this.localStorageService.intervalPlacing;
    this.selectedSnapToRoad = this.localStorageService.snapToRoad;
    this.checkNative();
  }

  private async checkNative() {
    this.native = this.platform.is('capacitor') && this.platform.is('android');
    this.cb.detectChanges();
  }

  public selectMapType() {
    this.localStorageService.mapType = this.selectedMapType;
  }

  public async back() {
    if (
      this.groupBindStore.accuratePositions &&
      this.groupBindStore.pointsPositions.start &&
      this.positioningSettingsChanged
    ) {
      const toast = await this.toastController.create({
        cssClass: 'toast-pointer-events-fix',
        header: 'Settings has been changed',
        message: 'You can restart positioning by clicking START POINT on a page below',
        position: 'top',
        duration: 5000,
      });
      toast.present();
    }
    this.modalController.dismiss();
  }

  public async confirmSignOut() {
    this.helper.confirm(this.signOut.bind(this), 'Are you sure you want to sign out?', 'Confirmation');
  }

  public async signOut() {
    this.groupBindStore.clearListsAndSelectedData();
    this.umStore.clearListsAndSelectedData();
    this.dmStore.clearListsAndSelectedData();
    this.localStorageService.clear();
    this.auth.signOut();
    this.maps.clearMap();
    this.modalController.dismiss();
  }

  public changeTheme() {
    this.localStorageService.darkMode = this.selectedDarkMode;
  }

  public selectAutocalibration() {
    this.localStorageService.autoCalibration = this.selectedAutocalibration;
  }

  public selectSnapToRoad() {
    if (this.localStorageService.snapToRoad !== this.selectedSnapToRoad) {
      this.positioningSettingsChanged = true;
    }
    this.localStorageService.snapToRoad = this.selectedSnapToRoad;
  }

  public selectInterval() {
    if (this.localStorageService.intervalPlacing !== this.selectedInterval) {
      this.positioningSettingsChanged = true;
    }
    this.localStorageService.intervalPlacing = this.selectedInterval;
  }

  public selectLightUpQR() {
    this.localStorageService.lightUpQR = this.selectedLightUpQR;
  }

  public selectCustomId() {
    this.localStorageService.customIdGeneration = this.selectedCustomId;
  }

  public selectIntervalM() {
    if (this.settingsControls.status === EValid.valid) {
      if (this.localStorageService.intervalMeters !== this.settingsControls.value.selectedIntervalMControl) {
        this.positioningSettingsChanged = true;
      }
      this.localStorageService.intervalMeters = this.settingsControls.value.selectedIntervalMControl || 0;
    }
  }

  private async deleteAccount() {
    const result = await this.umStore.deleteCurrentUser();

    if (result) {
      this.signOut();
    }
  }

  public confirmDeleteAccount() {
    this.helper.confirm(
      this.deleteAccount.bind(this),
      'All your account data will be removed. Proceed?',
      '',
      undefined,
      'Delete',
    );
  }
}
